import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api, ApiError,
  type IdeaGroup, type IdeaPoolResponse, type ResurfaceResponse,
} from '../api';
import { Button, Card, Empty, ErrorNote, Screen, Sheet, Skeleton, agoLabel, inputClass } from '../ui';
import { FindRecipeSheet } from './FindRecipe';
import { PlanSheet } from './Suggest';

/**
 * The Ideas screen (plan section 3.3, rebuilt in R12).
 *
 * The previous version showed 24 of 324 ideas as inert pills and nothing else,
 * which read as "the upload lost everything". It hadn't; the screen was hiding
 * it. Three things changed, in order of how much they mattered:
 *
 *   1. Nothing is hidden. The whole pool, searchable.
 *   2. Repeats are counted rather than listed. "Pizza x4" is a fact about this
 *      household; four "Pizza" rows are noise that buries the other 300.
 *   3. Every idea does something when tapped. An idea you cannot plan or cook
 *      is a museum piece.
 *
 * Suggest used to live here. It reads the whole corpus -- ideas, saved recipes
 * and what the household actually buys -- so tying its button to the idea pool
 * was wrong: promoting every idea to a recipe emptied the pool and took the
 * button with it, while the feature underneath still worked. It is on Recipes
 * now; see `Suggest.tsx`.
 */

type Tab = 'ideas' | 'shelf';

export function Ideas() {
  const [tab, setTab] = useState<Tab>('ideas');
  const [pool, setPool] = useState<IdeaPoolResponse | null>(null);
  const [shelf, setShelf] = useState<ResurfaceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<IdeaGroup | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, s] = await Promise.all([
        api.get<IdeaPoolResponse>('/ideas'),
        api.get<ResurfaceResponse>('/resurface'),
      ]);
      setPool(p);
      setShelf(s);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load ideas.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return pool?.ideas ?? [];
    return (pool?.ideas ?? []).filter((i) => i.idea_text.toLowerCase().includes(needle));
  }, [pool, q]);

  const dishes = pool?.ideas.length ?? 0;
  const lines = pool?.total ?? 0;

  return (
    <Screen
      title="Ideas"
      subtitle={
        pool
          ? `${dishes} dishes from your lists${lines > dishes ? ` · ${lines} lines read` : ''}`
          : undefined
      }
    >
      {error && <ErrorNote onRetry={() => void load()}>{error}</ErrorNote>}
      {!pool && !error && <Skeleton rows={5} />}

      {pool && (
        <div className="mb-4 flex gap-6 border-b border-border-subtle">
          <TabButton active={tab === 'ideas'} onClick={() => setTab('ideas')}>
            Your lists{dishes > 0 ? ` (${dishes})` : ''}
          </TabButton>
          <TabButton active={tab === 'shelf'} onClick={() => setTab('shelf')}>
            Forgotten{shelf && shelf.shelf.length > 0 ? ` (${shelf.shelf.length})` : ''}
          </TabButton>
        </div>
      )}

      {pool && tab === 'ideas' && (
        <>
          {dishes === 0 && (
            <Empty>
              Nothing here yet. Upload an old idea list or grocery list and every dish on it
              lands here.
            </Empty>
          )}

          {dishes > 0 && (
            <>
              <input aria-label="Search dishes" value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${dishes} dishes`}
                className={`${inputClass} mb-3`}
              />

              {filtered.length === 0 && (
                <p className="py-8 text-center text-sm text-text-muted">
                  Nothing matching “{q}”.
                </p>
              )}

              {/*
                One surface, hairline rows, no card per idea. A hundred and
                thirty-one bordered boxes is a wall; a list you can run your eye
                down is what this screen is for.
              */}
              <ul className="overflow-hidden rounded-card bg-bg-raised">
                {filtered.map((idea, idx) => (
                  <li key={idea.id} className={idx > 0 ? 'border-t border-border-subtle' : ''}>
                    <button
                      type="button"
                      onClick={() => setOpen(idea)}
                      className="flex w-full items-baseline gap-3 px-4 py-3.5 text-left
                                 transition-colors active:bg-bg-inset"
                    >
                      <span className="min-w-0 flex-1">
                        {/* Wraps rather than truncates. "Potato Broccoli Cheddar
                            Soup" and "Sweet Potato Black Bean Tacos" are real
                            entries here, and half a dish name is not an idea. */}
                        <span className="block break-words text-[17px] leading-snug text-text-primary">
                          {idea.idea_text}
                        </span>
                        {idea.notes && (
                          <span className="mt-0.5 block text-sm text-text-muted">{idea.notes}</span>
                        )}
                      </span>
                      {idea.count > 1 && (
                        /* The count is the whole reason for grouping -- the
                           closest thing to a preference ranking the archive
                           contains -- so it is set in the display face and
                           given room rather than shrunk into a badge. */
                        <span
                          className="numeric shrink-0 text-[15px] font-bold text-accent-vibrant"
                          title={`Written on ${idea.count} separate lists`}
                        >
                          ×{idea.count}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {pool && tab === 'shelf' && (
        <ShelfList data={shelf} />
      )}

      {open && (
        <IdeaSheet
          idea={open}
          onClose={() => setOpen(null)}
          onChanged={() => { setOpen(null); void load(); }}
        />
      )}

    </Screen>
  );
}

function TabButton({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tap relative -mb-px font-display text-[11px] uppercase tracking-[0.12em]
                  transition-colors ${active ? 'text-text-primary' : 'text-text-muted'}`}
    >
      {children}
      {/* A rule under the live tab rather than a filled pill: it marks position
          without adding another box to a screen made of rows. */}
      <span
        aria-hidden
        className={`absolute inset-x-0 -bottom-px h-[2px] rounded-full transition-opacity
                    ${active ? 'bg-accent-vibrant opacity-100' : 'opacity-0'}`}
      />
    </button>
  );
}

function ShelfList({ data }: { data: ResurfaceResponse | null }) {
  if (!data) return <Skeleton rows={3} />;

  if (data.shelf.length === 0) {
    return (
      <Empty>
        Nothing to resurface yet. This fills up as you mark dinners cooked — it is the
        “oh right, that!” shelf for things you loved and stopped making.
      </Empty>
    );
  }

  // With no cooking history this is just the recipe list. Say so rather than
  // implying these were surfaced for a reason.
  const noHistory = data.shelf.every((r) => r.last_served_at === null);

  return (
    <>
      {noHistory && (
        <p className="mb-3 rounded-card bg-accent-soft px-4 py-3 text-sm text-accent-deep">
          Nothing has been cooked in the app yet, so this is just everything saved. Mark a few
          dinners cooked and this becomes the forgotten-favorites shelf.
        </p>
      )}
      <ul className="space-y-2.5">
        {data.shelf.map((r) => (
          <li key={r.recipe_id}>
            <Card className="px-4 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <h2 className="min-w-0 font-medium leading-snug text-text-primary">{r.title}</h2>
                {r.avg_rating !== null && (
                  <span className="shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent-deep">
                    {r.avg_rating.toFixed(1)}★
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-sm text-text-muted">
                {agoLabel(r.days_since_served, r.last_served_at)}
                {r.times_served > 0 && ` · made ${r.times_served}×`}
              </p>
            </Card>
          </li>
        ))}
      </ul>
    </>
  );
}

// ------------------------------------------------------------- one idea

function IdeaSheet({ idea, onClose, onChanged }: {
  idea: IdeaGroup; onClose: () => void; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);
  const [finding, setFinding] = useState(false);

  async function promote() {
    setBusy(true); setError(null);
    try {
      await api.post('/ideas/promote', { ids: idea.ids });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
      setBusy(false);
    }
  }

  async function archive() {
    setBusy(true); setError(null);
    try {
      await api.post('/ideas/archive', { ids: idea.ids });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not archive that.');
      setBusy(false);
    }
  }

  if (planning) {
    return <PlanSheet dish={idea.idea_text} onClose={() => setPlanning(false)} onPlanned={onChanged} />;
  }

  if (finding) {
    return (
      <FindRecipeSheet idea={idea} onClose={() => setFinding(false)} onAdopted={onChanged} />
    );
  }

  return (
    <Sheet title={idea.idea_text} onClose={onClose}>
      <p className="text-sm text-text-muted">
        {idea.count > 1
          ? `Written on ${idea.count} separate lists — one of your regulars.`
          : 'From one of your uploaded lists.'}
      </p>
      {idea.notes && <p className="mt-2 text-sm text-text-primary">{idea.notes}</p>}

      {error && <div className="mt-3"><ErrorNote>{error}</ErrorNote></div>}

      <div className="mt-5 space-y-2">
        <Button className="w-full" disabled={busy} onClick={() => setPlanning(true)}>
          Put it on the week
        </Button>
        <Button variant="secondary" className="w-full" disabled={busy} onClick={() => setFinding(true)}>
          Find the recipe
        </Button>
        <p className="px-1 pt-1 text-xs text-text-muted">
          Searches the blogs you cook from — Cooking Classy, Half Baked Harvest, Pinch of Yum and
          eight more — and imports the one you pick, ingredients and all. This idea retires itself
          once a real recipe replaces it.
        </p>
        <Button variant="secondary" className="w-full" disabled={busy} onClick={() => void promote()}>
          {busy ? 'Working…' : 'Save the name only'}
        </Button>
        <p className="px-1 pt-1 text-xs text-text-muted">
          Creates a recipe with just this title. Your list didn’t record ingredients, and inventing
          them would put made-up food behind a real name.
        </p>
        <Button variant="danger" className="w-full" disabled={busy} onClick={() => void archive()}>
          Not interested
        </Button>
      </div>
    </Sheet>
  );
}
